import { ReactNode, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { formatDistanceToNow, isAfter } from 'date-fns';

import RoleChips from './RoleChips';
import { actionsCellSx, rolesCellSx, rowSx, TableCard } from './TableCard';
import { TeamInvite } from '../../lib/sdk';

const isCancelledInvite = (invite: TeamInvite): invite is TeamInvite & { cancelled_at: string } =>
  invite.cancelled_at !== undefined;

const isExpiredInvite = (invite: TeamInvite, now: Date): boolean => !isAfter(new Date(invite.expires_at), now);

const relativeTime = (date: string): string => formatDistanceToNow(new Date(date), { addSuffix: true });

type InviteRowProps = {
  invite: TeamInvite;
  tag?: JSX.Element;
  subtext: string;
  actions?: ReactNode;
};

function InviteRow({ invite, tag, subtext, actions }: InviteRowProps): JSX.Element {
  return (
    <Box sx={rowSx}>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ overflowWrap: 'anywhere' }}>{invite.invitee_email}</Typography>
          {tag}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {subtext}
        </Typography>
      </Box>
      <Box sx={rolesCellSx}>
        <RoleChips roles={invite.roles} />
      </Box>
      {actions !== undefined && <Box sx={actionsCellSx}>{actions}</Box>}
    </Box>
  );
}

type CancelInviteDialogProps = {
  open: boolean;
  email: string;
  isRequestPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

function CancelInviteDialog({
  open,
  email,
  isRequestPending,
  onClose,
  onConfirm,
}: CancelInviteDialogProps): JSX.Element {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Cancel invite?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Cancel the invite to <strong>{email}</strong>? Their invite link will stop working.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isRequestPending} onClick={onClose}>
          Keep invite
        </Button>
        <Button variant="contained" color="error" disabled={isRequestPending} onClick={onConfirm}>
          Cancel invite
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type TeamInvitesSectionProps = {
  invites: TeamInvite[];
  isRequestPending: boolean;
  resendInviteAction: (invite: TeamInvite) => Promise<boolean>;
  cancelInviteAction: (invite: TeamInvite) => Promise<boolean>;
};

export default function TeamInvitesSection({
  invites,
  isRequestPending,
  resendInviteAction,
  cancelInviteAction,
}: TeamInvitesSectionProps): JSX.Element | null {
  const [inviteToCancel, setInviteToCancel] = useState<TeamInvite>();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);

  if (invites.length === 0) {
    return null;
  }

  const now = new Date();
  const pendingInvites = invites.filter((invite) => !isCancelledInvite(invite) && !isExpiredInvite(invite, now));
  const expiredInvites = invites.filter((invite) => !isCancelledInvite(invite) && isExpiredInvite(invite, now));
  const cancelledInvites = invites.filter(isCancelledInvite);
  const counts = [
    pendingInvites.length > 0 && `${pendingInvites.length} pending`,
    expiredInvites.length > 0 && `${expiredInvites.length} expired`,
    cancelledInvites.length > 0 && `${cancelledInvites.length} cancelled`,
  ]
    .filter(Boolean)
    .join(', ');

  // on mobile the buttons fill the row
  const resendButton = (invite: TeamInvite) => (
    <Button
      variant="outlined"
      size="small"
      disabled={isRequestPending}
      onClick={() => resendInviteAction(invite)}
      sx={{ flex: { xs: 1, sm: 'none' } }}
    >
      Resend
    </Button>
  );

  const openCancelDialog = (invite: TeamInvite) => {
    setInviteToCancel(invite);
    setIsCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (inviteToCancel === undefined) {
      return;
    }
    await cancelInviteAction(inviteToCancel);
    setIsCancelDialogOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="h6">Invites</Typography>
        {counts !== '' && <Typography color="text.secondary">· {counts}</Typography>}
      </Box>
      <TableCard firstHeader="Email">
        {pendingInvites.map((invite) => (
          <InviteRow
            key={invite.invite_id}
            invite={invite}
            subtext={`Expires ${relativeTime(invite.expires_at)}`}
            actions={
              <>
                {resendButton(invite)}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={isRequestPending}
                  onClick={() => openCancelDialog(invite)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Cancel
                </Button>
              </>
            }
          />
        ))}
        {expiredInvites.map((invite) => (
          <InviteRow
            key={invite.invite_id}
            invite={invite}
            tag={<Chip label="Expired" size="small" color="error" />}
            subtext={`Expired ${relativeTime(invite.expires_at)}`}
            actions={resendButton(invite)}
          />
        ))}
        {cancelledInvites.map((invite) => (
          <InviteRow
            key={invite.invite_id}
            invite={invite}
            tag={<Chip label="Cancelled" size="small" />}
            subtext={`Cancelled ${relativeTime(invite.cancelled_at)}`}
          />
        ))}
      </TableCard>
      <Typography variant="body2" color="text.secondary">
        Expired and cancelled invites are shown for 90 days.
      </Typography>
      <CancelInviteDialog
        open={isCancelDialogOpen}
        email={inviteToCancel?.invitee_email ?? ''}
        isRequestPending={isRequestPending}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={confirmCancel}
      />
    </Box>
  );
}
